import ServiceTemplate from './service-page-template/service-page-template.jsx';
import { imagesMetadata } from '../../imagesMetadata.js';

const heroImageSrc = '/static/images/home-maintenance/moorabbin-high-pressure-washing-01.webp';
const heroMeta = imagesMetadata[heroImageSrc];

const excludedImages = [
  heroImageSrc,
  // Add any other image paths to exclude here...
];

const homeMaintenanceGalleryImages = Object.entries(imagesMetadata)
  .filter(([src]) => src.startsWith('/static/images/home-maintenance/') && !excludedImages.includes(src))
  .map(([src, meta], index) => ({
    id: index + 1,
    src,
    alt: meta.alt,
    location: meta.location
  }));

export default function HomeMaintenance() {
  const servicesData = {
    title: "Maintenance Services",
    subtitle: "Reliable ongoing home maintenance and odd jobs to keep your property in top condition.",
    metaTitle: "Home Maintenance Melbourne | Repairs & Property Upkeep",
    metaDesc: "Home maintenance in Melbourne for minor repairs, odd jobs, pressure cleaning and routine property upkeep. Book a local handyman for your to-do list.",
    canonicalPath: "/home-maintenance",

    solutionsTitle: "Our Home Maintenance Services",
    solutionsIntro: "We help homeowners, landlords and businesses stay on top of regular upkeep and small repairs before they become bigger problems.",

    solutionsList: [
      { 
        emoji: "🧼", 
        heading: "High Pressure Cleaning", 
        text: "Driveways, paths, decks, fences, exterior walls, and pavers washed to remove dirt, moss, and grime." 
      },
      { 
        emoji: "🔧", 
        heading: "General Odd Jobs & Repairs", 
        text: "Small fixes around the home that you never seem to get around to." 
      },
      { 
        emoji: "🚰", 
        heading: "Minor Plumbing & Leaks", 
        text: "Fixing dripping taps, running toilets, and minor plumbing issues." 
      },
      { 
        emoji: "🏡", 
        heading: "Seasonal & Property Maintenance", 
        text: "Gutter checks, window & flyscreen repairs, door adjustments, and general property tune-ups." 
      }
    ],

    serviceImage: {
      src: heroImageSrc,
      alt: heroMeta?.alt || 'External high pressure cleanning before open house inspection in Moorabbin by Handyman In Melbourne'
    },

    extendedDetails: [
      {
        title: "Preventative Maintenance Focus",
        body: "We help you catch and fix small issues early — preventing costly repairs later and keeping your home safe, efficient, and comfortable year-round."
      },
      {
        title: "Reliable & Consistent Service",
        body: "Whether it's a one-off job or regular maintenance visits, we show up on time, communicate clearly, and complete the work to a high standard."
      },
      {
        title: "Experience Across All Home Types",
        body: "From period homes in inner Melbourne to modern townhouses and apartments in the suburbs, we understand the common maintenance needs of different property styles."
      },
      {
        title: "Transparent & Efficient Work",
        body: "Clear fixed-price quotes, minimal disruption to your day, and thorough cleanup after every visit."
      }
    ],

    galleryImages: homeMaintenanceGalleryImages,

    faqs: [
      {
        question: 'What does a typical home maintenance visit include?',
        answer: 'Checking smoke alarms, safety switches, taps, toilets, doors, windows, and general wear & tear.'
      },
      {
        question: 'Can you fix leaking taps and running toilets?',
        answer: 'Yes. These are very common quick fixes.'
      },
      {
        question: 'How often should I schedule maintenance?',
        answer: 'Most homeowners benefit from 6-monthly or annual visits.'
      },
    ],
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": servicesData.title,
    "serviceType": servicesData.title,
    "description": servicesData.metaDesc,
    "url": `https://handymaninmelbourne.au${servicesData.canonicalPath}`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Handyman In Melbourne",
      "url": "https://handymaninmelbourne.au/"
    },
    "areaServed": [
            "City of Melbourne VIC",
            "City of Port Phillip VIC",
            "City of Glen Eira VIC",
            "City of Stonnington VIC",
            "City of Boroondara VIC",
            "City of Whitehorse VIC",
            "City of Manningham VIC",
            "City of Monash VIC",
            "City of Kingston VIC",
            "City of Yarra VIC",
            "City of Moonee Valley VIC",
            "City of Darebin VIC",
            "City of Greater Dandenong VIC"
        ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <ServiceTemplate {...servicesData} />
    </>
  );
}
